'use client'

import { useState, Suspense } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { ArrowLeft, Mail, Lock, CheckCircle } from 'lucide-react'
import Link from 'next/link'

export default function ResetPasswordPageWrapper() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-installer-bg" />}>
      <ResetPasswordPage />
    </Suspense>
  )
}

function ResetPasswordPage() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const token = searchParams.get('token')

  // Request form state
  const [email, setEmail] = useState('')
  const [requestSent, setRequestSent] = useState(false)

  // Confirm form state
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resetDone, setResetDone] = useState(false)

  // Shared state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Fehler beim Senden')
      }

      setRequestSent(true)
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (newPassword.length < 12) {
      setError('Passwort muss mindestens 12 Zeichen lang sein')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein')
      return
    }

    setLoading(true)

    try {
      const res = await fetch('/api/auth/reset-password/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Fehler beim Zurücksetzen')
      }

      setResetDone(true)
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-installer-bg">
      {/* Background gradient — same as login page */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-installer-bg via-installer-bg to-emerald-dark/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-heiz-orange/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo area */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <svg width="48" height="48" viewBox="0 0 44 44" fill="none"><rect x="3" y="7" width="34" height="26" fill="#FFD400"/><path d="M30 33 L37 33 L37 40 Z" fill="#FFD400"/><text x="20" y="27" textAnchor="middle" fontSize="19" fontWeight="800" letterSpacing="-0.8" fill="#111111">43</text></svg>
            <h1 className="text-2xl font-bold text-white">
              43<span className="text-heiz-orange">OS</span>
            </h1>
          </div>
          <p className="text-gray-1 text-sm mt-2">Passwort zurücksetzen</p>
        </div>

        {/* Card */}
        <div className="bg-installer-card border border-installer-border rounded-2xl p-8 shadow-2xl">

          {/* ── Mode: Token present — set new password ── */}
          {token && !resetDone && (
            <>
              <h2 className="text-xl font-semibold text-white mb-2">Neues Passwort</h2>
              <p className="text-gray-1 text-sm mb-6">Geben Sie Ihr neues Passwort ein.</p>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleConfirmReset}>
                <div className="mb-4">
                  <label className="block text-sm text-gray-1 mb-1.5">Neues Passwort</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-1/50" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Mindestens 12 Zeichen"
                      required
                      minLength={12}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                                 text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                                 focus:ring-heiz-orange/30 outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-gray-1 mb-1.5">Passwort bestätigen</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-1/50" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Passwort wiederholen"
                      required
                      minLength={12}
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                                 text-white placeholder:text-gray-1/50 focus:border-emerald focus:ring-1
                                 focus:ring-emerald/30 outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                             bg-gradient-to-r from-heiz-orange to-[#c43a22] text-white font-semibold
                             hover:from-[#FFE74A] hover:to-heiz-orange transition-all disabled:opacity-50
                             disabled:cursor-not-allowed shadow-[0_0_20px_rgba(255,212,0,0.3)] text-sm"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Passwort speichern'
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── Mode: Token present — reset done ── */}
          {token && resetDone && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Passwort geändert</h2>
              <p className="text-gray-1 text-sm mb-6">
                Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt einloggen.
              </p>
              <Link
                href="/installer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl
                           bg-gradient-to-r from-heiz-orange to-[#c43a22] text-white font-semibold
                           hover:from-[#FFE74A] hover:to-heiz-orange transition-all text-sm
                           shadow-[0_0_20px_rgba(255,212,0,0.3)]"
              >
                Zum Login
              </Link>
            </div>
          )}

          {/* ── Mode: No token — request email ── */}
          {!token && !requestSent && (
            <>
              <h2 className="text-xl font-semibold text-white mb-2">Passwort vergessen?</h2>
              <p className="text-gray-1 text-sm mb-6">
                Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen.
              </p>

              {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleRequestReset}>
                <div className="mb-6">
                  <label className="block text-sm text-gray-1 mb-1.5">E-Mail</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-1/50" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@firma.de"
                      required
                      className="w-full pl-10 pr-4 py-3 rounded-xl bg-installer-surface border border-installer-border
                                 text-white placeholder:text-gray-1/50 focus:border-heiz-orange focus:ring-1
                                 focus:ring-heiz-orange/30 outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl
                             bg-gradient-to-r from-heiz-orange to-[#c43a22] text-white font-semibold
                             hover:from-[#FFE74A] hover:to-heiz-orange transition-all disabled:opacity-50
                             disabled:cursor-not-allowed shadow-[0_0_20px_rgba(255,212,0,0.3)] text-sm"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Link senden
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── Mode: No token — email sent confirmation ── */}
          {!token && requestSent && (
            <div className="text-center py-4">
              <Mail className="w-12 h-12 text-heiz-orange mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">E-Mail gesendet</h2>
              <p className="text-gray-1 text-sm mb-2">
                Falls ein Konto mit dieser Adresse existiert, erhalten Sie in Kürze eine E-Mail mit einem Link zum Zurücksetzen.
              </p>
              <p className="text-gray-1/60 text-xs">
                Prüfen Sie auch Ihren Spam-Ordner.
              </p>
            </div>
          )}

          {/* Back to login link */}
          <div className="mt-6 text-center">
            <Link
              href="/installer"
              className="inline-flex items-center gap-1.5 text-xs text-gray-1 hover:text-heiz-orange transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Zurück zum Login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-1/50 mt-4">
          43OS v1.0
        </p>
      </div>
    </div>
  )
}
